import config from 'constants/config'
import GeoContext from 'contexts/geoContext'
import moment from 'moment'
import { useContext } from 'react'
import { bestOfRegions } from './BestOfConstants'
import { useAppSelector } from 'hooks/reduxHooks'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

export const firstWaveEarlyAccessDate = moment('2024-12-23T06:00:00+1100')
export const firstWaveStartDate = moment('2024-12-26T06:00:00+1100')
export const secondWaveEarlyAccessDate = moment('2024-12-30T06:00:00+1100')
export const secondWaveStartDate = moment('2025-01-02T06:00:00+1100')
export const thirdWaveEarlyAccessDate = moment('2025-01-06T06:00:00+1100')
export const thirdWaveStartDate = moment('2025-01-09T06:00:00+1100')
export const bestOfEndDate = moment('2025-01-15T23:59:00+1100')

/**
 * The best of content will be coming in 'waves'
 * This is a list of all the different waves that will be coming
 *
 * Early release waves are where lux plus members get access to the offers before the wave starts
 * Non lux-plus members will only see a "hidden offers" carousel
 *
 * During the wave itself, all offers will be seen
 */
export type BestOf2024Wave = 'first-wave-early-access' | 'first-wave' | 'second-wave-early-access' | 'second-wave' | 'third-wave-early-access' | 'third-wave'

export const EarlyAccessWaves = new Set<BestOf2024Wave>([
  'first-wave-early-access',
  'second-wave-early-access',
  'third-wave-early-access',
])

export function getBestOf2024Wave(regionCode: string): BestOf2024Wave | undefined {
  if (typeof window !== 'undefined' && !!(window as any).__BESTOFWAVEDEBUG__) {
    // give a way to test it
    return (window as any).__BESTOFWAVEDEBUG__
  }

  // make sure it's enabled for the brand and is a valid region
  if (config.brand !== 'luxuryescapes' || !bestOfRegions.has(regionCode)) {
    return undefined
  }

  const now = moment()
  if (now.isBefore(firstWaveEarlyAccessDate) || now.isAfter(bestOfEndDate)) {
    // outside of the range of dates, so no wave
    return undefined
  }

  if (now.isSameOrAfter(thirdWaveStartDate)) {
    return 'third-wave'
  } else if (now.isSameOrAfter(thirdWaveEarlyAccessDate)) {
    return 'third-wave-early-access'
  } else if (now.isSameOrAfter(secondWaveStartDate)) {
    return 'second-wave'
  } else if (now.isSameOrAfter(secondWaveEarlyAccessDate)) {
    return 'second-wave-early-access'
  } else if (now.isSameOrAfter(firstWaveStartDate)) {
    return 'first-wave'
  } else if (now.isSameOrAfter(firstWaveEarlyAccessDate)) {
    return 'first-wave-early-access'
  }
}

interface BestOfConfig {
  enabled: boolean;
  wave: BestOf2024Wave | undefined;
  isEarlyAccess: boolean;
}

export function useBestOf2024(): BestOfConfig {
  const { currentRegionCode } = useContext(GeoContext)
  const wave = getBestOf2024Wave(currentRegionCode)
  const isLuxMember = useAppSelector(checkCanViewLuxPlusBenefits)

  return {
    enabled: isLuxMember ? !!wave : (!!wave && wave !== 'first-wave-early-access'),
    isEarlyAccess: !!wave && EarlyAccessWaves.has(wave),
    wave,
  }
}
